import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../utils/api'

// Capability Map store (Feature1 capability growth integration).
//
// Owns the async lifecycle for the synced capability projection: the per-site
// coverage rollup (mentions / posts / campaigns / results per capability) and
// the admin-triggered sync from Feature1. Mirrors the hardening invariants of
// analyticsWorkspace.js:
//   • newest-response-wins: each coverage fetch carries a monotonic sequence;
//     a slower earlier response is dropped so it cannot clobber a newer one.
//   • error classification: transport/timeout/server failures map to
//     actionable, non-raw messages.
export const useCapabilitiesStore = defineStore('capabilities', () => {
  const coverage = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const errorKind = ref(null) // 'timeout' | 'network' | 'server' | 'generic'

  // Sync-trigger flow (admin only server-side; the button hides for members).
  const syncing = ref(false)
  const syncError = ref(null)
  const lastSync = ref(null)

  // Mention-scan flow: an async Codex run server-side — the trigger returns a
  // run id immediately (202) and the store polls until it lands.
  const scanning = ref(false)
  const scanError = ref(null)
  const scanRun = ref(null)

  // Results-collection flow: async Postiz engagement pull, same trigger+poll
  // contract as the scan.
  const collecting = ref(false)
  const collectError = ref(null)
  const collectRun = ref(null)

  // aria-live announcement text for assistive tech.
  const announce = ref('')

  let coverageSeq = 0

  const capabilities = computed(() => coverage.value?.capabilities || [])
  const summary = computed(() => coverage.value?.summary || { total: 0, covered: 0, partial: 0, uncovered: 0 })

  // Capabilities grouped persona → module for the map layout.
  const grouped = computed(() => {
    const personas = new Map()
    for (const cap of capabilities.value) {
      const personaKey = cap.persona_name || 'Unmapped persona'
      const moduleKey = cap.module_name || 'Unmapped module'
      if (!personas.has(personaKey)) personas.set(personaKey, new Map())
      const modules = personas.get(personaKey)
      if (!modules.has(moduleKey)) modules.set(moduleKey, [])
      modules.get(moduleKey).push(cap)
    }
    return [...personas.entries()].map(([persona, modules]) => ({
      persona,
      modules: [...modules.entries()].map(([module, caps]) => ({ module, capabilities: caps })),
    }))
  })

  // Single explicit state discriminator — exactly one of these renders.
  const uiState = computed(() => {
    if (loading.value) return 'loading'
    if (error.value) return 'error'
    if (!capabilities.value.length) return 'empty'
    return 'ready'
  })

  function classifyError(err) {
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return { kind: 'timeout', message: 'The request timed out. Check your connection and retry.' }
    }
    if (!err?.response) {
      return { kind: 'network', message: 'Network error — you appear to be offline. Retry when reconnected.' }
    }
    if (err.response.status >= 500) {
      return { kind: 'server', message: 'Capabilities are temporarily unavailable. Please retry shortly.' }
    }
    return { kind: 'generic', message: err.response?.data?.error || 'Something went wrong. Please retry.' }
  }

  async function loadCoverage(siteId) {
    const seq = ++coverageSeq
    loading.value = true
    error.value = null
    errorKind.value = null
    try {
      const { data } = await api.get(`/sites/${siteId}/capabilities/coverage`)
      if (seq !== coverageSeq) return // stale response — newest wins
      coverage.value = data.coverage || null
      announce.value = 'Capability map updated'
    } catch (err) {
      if (seq !== coverageSeq) return
      const c = classifyError(err)
      errorKind.value = c.kind
      error.value = c.message
      announce.value = 'Failed to load the capability map'
    } finally {
      if (seq === coverageSeq) loading.value = false
    }
  }

  // Trigger a tenant-scoped, additive, idempotent sync from Feature1, then
  // refresh coverage. Sync failures never clear what is already on screen.
  async function syncNow(siteId) {
    syncing.value = true
    syncError.value = null
    try {
      const { data } = await api.post('/capabilities/sync')
      lastSync.value = data.sync || null
      announce.value = `Capability sync complete — ${data.sync?.total ?? 0} capabilities`
      await loadCoverage(siteId)
      return data.sync
    } catch (err) {
      const code = err?.response?.data?.details?.code
      syncError.value = code === 'feature1_not_configured'
        ? 'Feature1 is not connected for this workspace yet. Connect it in Settings.'
        : code === 'feature1_token_invalid'
          ? 'The Feature1 token expired or was revoked. Reconnect Feature1 in Settings.'
          : classifyError(err).message
      announce.value = 'Capability sync failed'
      return null
    } finally {
      syncing.value = false
    }
  }

  // Trigger a mention scan (admin): Codex reads the site repo + social post
  // texts server-side and writes source='detected' capability links. Poll the
  // run until it's terminal, then refresh coverage. Scan failures never clear
  // what is already on screen.
  const SCAN_POLL_MS = 3000
  const SCAN_MAX_POLLS = 200 // ~10 minutes

  async function scanMentions(siteId) {
    scanning.value = true
    scanError.value = null
    try {
      const { data } = await api.post(`/sites/${siteId}/capabilities/scan`)
      scanRun.value = data.run || null
      announce.value = data.reused ? 'A mention scan is already running' : 'Mention scan started'
      if (data.run?.id) await pollScan(siteId, data.run.id)
      return scanRun.value
    } catch (err) {
      scanError.value = classifyError(err).message
      announce.value = 'Mention scan failed to start'
      return null
    } finally {
      scanning.value = false
    }
  }

  async function pollScan(siteId, runId) {
    for (let i = 0; i < SCAN_MAX_POLLS; i++) {
      await new Promise((resolve) => setTimeout(resolve, SCAN_POLL_MS))
      try {
        const { data } = await api.get(`/sites/${siteId}/capabilities/scan/${runId}`)
        scanRun.value = data.run || scanRun.value
        if (data.run?.status === 'success') {
          announce.value = 'Mention scan complete'
          await loadCoverage(siteId)
          return
        }
        if (data.run?.status === 'failed') {
          scanError.value = `Scan failed: ${data.run.failure_reason || 'unknown reason'}`
          announce.value = 'Mention scan failed'
          return
        }
      } catch (err) {
        // Transient poll errors are tolerated; the loop keeps trying.
      }
    }
    scanError.value = 'The scan is taking longer than expected — results will appear on the next reload.'
  }

  // Collect social engagement results (admin): the backend pulls post-level
  // analytics from Postiz, appends metric snapshots, and refreshes the
  // 'result' links on capability-linked posts.
  async function collectResults(siteId) {
    collecting.value = true
    collectError.value = null
    try {
      const { data } = await api.post(`/sites/${siteId}/social-metrics/collect`)
      collectRun.value = data.run || null
      announce.value = data.reused ? 'A results collection is already running' : 'Results collection started'
      if (data.run?.id) await pollCollect(siteId, data.run.id)
      return collectRun.value
    } catch (err) {
      collectError.value = classifyError(err).message
      announce.value = 'Results collection failed to start'
      return null
    } finally {
      collecting.value = false
    }
  }

  async function pollCollect(siteId, runId) {
    for (let i = 0; i < SCAN_MAX_POLLS; i++) {
      await new Promise((resolve) => setTimeout(resolve, SCAN_POLL_MS))
      try {
        const { data } = await api.get(`/sites/${siteId}/social-metrics/collect/${runId}`)
        collectRun.value = data.run || collectRun.value
        if (data.run?.status === 'success') {
          announce.value = 'Results collection complete'
          await loadCoverage(siteId)
          return
        }
        if (data.run?.status === 'failed') {
          collectError.value = data.run.failure_reason === 'postiz_not_configured'
            ? 'Postiz is not connected for this workspace — connect it on the Social page first.'
            : `Collection failed: ${data.run.failure_reason || 'unknown reason'}`
          announce.value = 'Results collection failed'
          return
        }
      } catch (err) {
        // Transient poll errors are tolerated; the loop keeps trying.
      }
    }
    collectError.value = 'The collection is taking longer than expected — results will appear on the next reload.'
  }

  return {
    coverage, loading, error, errorKind,
    syncing, syncError, lastSync, announce,
    scanning, scanError, scanRun,
    collecting, collectError, collectRun,
    capabilities, summary, grouped, uiState,
    loadCoverage, syncNow, scanMentions, collectResults,
  }
})
